/**
 * The canonical link to a workspace-shaped resource, declared ONCE for the two
 * sides that need it (PB-1583).
 *
 * A share/invite notification told the recipient something was shared and gave
 * them no way to reach it: the `workspace.*` producers set no `href`, so the
 * feed row rendered as a plain button. The registry has carried the intended
 * shape all along — `hrefRoute: "/app/{module}/{entityId}"` — but nothing
 * substituted `{module}`, and a producer guessing at it would have stored a
 * literal `{module}` in a user-visible link with no error anywhere.
 *
 * Same reasoning as `lama-routes.ts`: the producer and the shell each used to
 * be able to spell a route out, and two independent spellings of one route
 * means a rename silently breaks every link already sent. This carries no
 * behaviour, only the shape of the URL, so neither side gains a dependency on
 * the other.
 *
 * **This is a REFERENCE, not the final route.** `/app/solve/<solveId>` is not
 * where a SOLVE workspace lives — that is `/app/solve/<tabInstance>/<solveId>`,
 * and the tab instance is minted by the shell and unknowable to a server. So
 * the shell resolves this reference through its existing entity-tab contract
 * (`openModuleEntityTab`), which already knows each module's route policy.
 * Opened as an address (a new browser tab, a copied link), the shell's
 * start-up opens a SOLVE or VSM reference under a numbered tab the same way.
 */
/** The shell route base of each module that can be linked to this way. */
const WORKSPACE_MODULE_BASE = {
    solve: "/app/solve",
    vsm: "/app/vsm",
    hoshin: "/app/hoshin",
};
export function isLinkableWorkspaceModule(moduleId) {
    return Object.prototype.hasOwnProperty.call(WORKSPACE_MODULE_BASE, moduleId);
}
/**
 * A reference to ONE resource: `/app/<module>/<entityId>`.
 *
 * Returns `null` for a module whose resources this shape cannot address, so a
 * caller has to handle the absence rather than receive a plausible-looking
 * path to nowhere.
 */
export function workspaceResourceHref(moduleId, entityId) {
    if (!isLinkableWorkspaceModule(moduleId))
        return null;
    return `${WORKSPACE_MODULE_BASE[moduleId]}/${encodeURIComponent(entityId)}`;
}
/**
 * A reference to a module's own landing surface: `/app/<module>`.
 *
 * What "you were removed" links to — the registry's `hrefRoute` for
 * `workspace.member.removed` is `"/app/{module}"` for exactly this reason: the
 * recipient can no longer open the resource, so a deep link would be a
 * deliberate 404.
 */
export function workspaceModuleHref(moduleId) {
    if (!isLinkableWorkspaceModule(moduleId))
        return null;
    return WORKSPACE_MODULE_BASE[moduleId];
}
/**
 * Read a reference back: the inverse of {@link workspaceResourceHref}.
 *
 * The shell uses this to route a notification through its entity-tab contract
 * instead of navigating to the literal path. Deliberately strict — exactly two
 * segments after `/app`, a non-empty id, and a known module — so a path with a
 * deeper tail (a real, already-resolved route) falls through to plain
 * navigation rather than being truncated to its first two segments.
 */
export function parseWorkspaceResourceHref(href) {
    const [path] = href.split(/[?#]/, 1);
    if (path === undefined)
        return null;
    const segments = path.split("/").filter((segment) => segment.length > 0);
    if (segments.length !== 3 || segments[0] !== "app")
        return null;
    const [, moduleId, rawEntityId] = segments;
    if (moduleId === undefined ||
        rawEntityId === undefined ||
        !isLinkableWorkspaceModule(moduleId)) {
        return null;
    }
    // `decodeURIComponent` THROWS on malformed percent encoding (`%E0%A4%A`), and
    // this runs on the notification-activation path — an unparseable href has to
    // fall through to the caller's literal-navigation branch, not abort the
    // click with a `URIError`.
    let entityId;
    try {
        entityId = decodeURIComponent(rawEntityId);
    }
    catch {
        return null;
    }
    if (entityId.length === 0)
        return null;
    return { moduleId, entityId };
}
//# sourceMappingURL=workspace-routes.js.map